import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Hourglass, Loader2, Save } from "lucide-react";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";

const left = (until) => Math.max(0, Math.ceil((new Date(until) - Date.now()) / 86400000));

// Mini-site client: trial badge + one-tap extension (+3 / +7 days).
export const TrialTools = ({ d, onChanged }) => {
  const { t, fmtDate } = useI18n();
  const [busy, setBusy] = useState(null);
  if (d?.kind !== "minisite") return null;
  const running = d.trial_until && new Date(d.trial_until) > new Date();
  const extend = async (days) => {
    setBusy(days);
    try {
      const { data } = await api.post(`/admin/minisite/clients/${d.id}/trial/extend`, { days });
      toast.success(t("trial.extended", { date: fmtDate(data.trial_until) }));
      onChanged?.();
    } catch (err) {
      toast.error(translateError(err, t));
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="flex min-w-0 flex-wrap items-center gap-2" data-testid={`trial-tools-${d.id}`}>
      <span className={`inline-flex max-w-full items-center gap-1 break-words rounded-full px-2 py-0.5 text-[0.65rem] font-bold uppercase ${running ? "bg-emerald-500/15 text-emerald-200" : "bg-neutral-700/40 text-neutral-300"}`} data-testid={`trial-badge-${d.id}`}>
        <Hourglass className="h-3 w-3 shrink-0" /> {d.trial_until ? t(running ? "trial.daysLeft" : "trial.ended", { n: left(d.trial_until), date: fmtDate(d.trial_until) }) : t("trial.none")}
      </span>
      {d.trial_repeat && <span className="rounded-full bg-red-500/15 px-2 py-0.5 text-[0.65rem] font-bold uppercase text-red-200" data-testid={`trial-repeat-${d.id}`}>{t("trial.repeat", { install: d.trial_install || "—" })}</span>}
      {[3, 7].map((n) => (
        <button key={n} type="button" className="btn-red btn-sm" disabled={busy !== null} onClick={() => extend(n)} data-testid={`trial-extend-${n}-${d.id}`}>
          {busy === n && <Loader2 className="h-4 w-4 animate-spin" />} {t("trial.extend", { n })}
        </button>
      ))}
    </div>
  );
};

export const TrialSettingsCard = () => {
  const { t } = useI18n();
  const [days, setDays] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/minisite/trial-config").then(({ data }) => setDays(String(data.days))).catch(() => {}); }, []);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/admin/minisite/trial-config", { days: Number(days) });
      toast.success(t("trial.saved"));
    } catch (err) {
      toast.error(translateError(err, t));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={save} className="card-carbon flex min-w-0 flex-wrap items-end gap-3 rounded-2xl p-5" data-testid="trial-settings-card">
      <label className="min-w-0 flex-1 basis-56 text-xs text-neutral-400">
        <span className="break-words">{t("trial.setting")}</span>
        <Input inputMode="numeric" value={days} onChange={(e) => setDays(e.target.value.replace(/\D/g, ""))} className="mt-1 bg-black/50" data-testid="trial-days-input" />
      </label>
      <button type="submit" className="btn-gold btn-sm" disabled={busy || !days} data-testid="trial-days-save">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {t("mini.save")}
      </button>
    </form>
  );
};
